/**
 * "Download PDF": stamp the plan onto the original LBA form. The FORM stays
 * German in both interface languages — it is the LBA original, and the file name
 * is the German one pilots file it under.
 *
 * The template lives in /public and is fetched at click time, not bundled — a
 * 25 kB PDF has no business in the JS bundle, and the browser caches it after
 * the first download. The stamper (pdf-lib) is loaded the same way, for the same
 * reason at forty times the size: see `buildPdf`.
 */
import { LBA_BOLD_FIELDS, LBA_FIELDS } from '@avia/core';

const TEMPLATE_URL = '/lba.pdf';

let templateCache: Uint8Array | null = null;

async function lbaTemplate(): Promise<Uint8Array> {
  if (templateCache) return templateCache;
  const res = await fetch(TEMPLATE_URL, { cache: 'force-cache' });
  // shown inside the translated "PDF could not be created: …" alert
  if (!res.ok) throw new Error(TEMPLATE_URL + ' HTTP ' + res.status);
  templateCache = new Uint8Array(await res.arrayBuffer());
  return templateCache;
}

/** `Flugdurchfuehrungsplan_EDMA-EDKS_9-5-87.pdf` */
export function pdfFilename(values: Record<string, string>): string {
  const clean = (s: string | undefined) => String(s ?? '').replace(/[^\wÄÖÜäöüß-]+/g, '').slice(0, 12);
  const von = clean(values['von']);
  const nach = clean(values['nach']);
  const datum = String(values['datum'] ?? '').replace(/[^\d]+/g, '-').replace(/^-|-$/g, '');
  let name = 'Flugdurchfuehrungsplan';
  if (von && nach) name += '_' + von + '-' + nach;
  if (datum) name += '_' + datum;
  return name + '.pdf';
}

export async function buildPdf(values: Record<string, string>): Promise<Uint8Array> {
  // The stamper carries pdf-lib (~1 MB). It is fetched on the first download,
  // not on the first paint — the same reason the template itself is not bundled.
  const [{ stampPdf }, template] = await Promise.all([import('@avia/core/pdf'), lbaTemplate()]);
  return stampPdf(template, LBA_FIELDS, values, { bold: LBA_BOLD_FIELDS });
}

export async function downloadPdf(values: Record<string, string>): Promise<void> {
  const bytes = await buildPdf(values);
  // Detach the view from its (possibly shared) backing buffer — Blob wants a plain ArrayBuffer.
  const buffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
  const blob = new Blob([buffer], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = pdfFilename(values);
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    a.remove();
  }, 4000);
}
